import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Layers, Loader2, Send, Trash2, ExternalLink, CheckCircle2, Target } from 'lucide-react';
import { useCan } from '@/lib/permissions';

const APPR_LABEL = {
  none: 'Not routed', pending_approval: 'Pending approval', approved: 'Approved',
  actioned: 'Actioned', rejected: 'Rejected',
};

function Chip({ children, tone = 'muted' }) {
  const tones = {
    muted: 'border-border/40 text-muted-foreground',
    green: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
    amber: 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300',
    violet: 'border-primary/30 bg-primary/10 text-primary',
  };
  return <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] border ${tones[tone]}`}>{children}</span>;
}

export default function InitiativesPage() {
  const navigate = useNavigate();
  const canWrite = useCan('measures:certify');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.listInitiatives(); setItems(r.initiatives || []); }
    catch (e) { toast.error(e.message || 'Could not load initiatives'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const routeAll = async (it) => {
    setBusy(it.initiative_id);
    try {
      const r = await api.routeInitiative(it.initiative_id);
      toast.success(`Routed ${r.routed} material recommendation${r.routed === 1 ? '' : 's'} to approval.`);
      await load();
    } catch (e) { toast.error(e.message || 'Could not route initiative'); }
    finally { setBusy(''); }
  };
  const remove = async (it) => {
    try { await api.deleteInitiative(it.initiative_id); toast.success('Initiative removed'); load(); }
    catch (e) { toast.error(e.message || 'Could not remove'); }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="initiatives-page">
      <div className="mb-4">
        <h1 className="text-2xl font-bold flex items-center gap-2"><Layers className="w-6 h-6 text-primary" /> Initiatives</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Bundles of related recommendations reviewed, approved and tracked together. Create an initiative from the
          Governed Recommendations (select recs → Group into initiative), then route its material members to approval as one.
          Nothing auto-executes.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-muted-foreground py-12 text-center" data-testid="initiatives-empty">
          No initiatives yet. Open the Governed Recommendations, turn on Select, tick related recommendations and choose “Group into initiative”.
          <div className="mt-3"><Button size="sm" variant="outline" onClick={() => navigate('/recommendations')} data-testid="initiatives-goto-hub">Go to Governed Recommendations</Button></div>
        </div>
      ) : (
        <div className="space-y-4" data-testid="initiatives-list">
          {items.map((it) => {
            const roll = it.rollup || {};
            const appr = roll.approvals || {};
            const out = roll.outcomes || {};
            const materialCount = (it.members || []).filter((m) => m.material).length;
            return (
              <div key={it.initiative_id} className="bg-card/60 border border-border/40 rounded-2xl p-4" data-testid={`initiative-card-${it.initiative_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-heading font-semibold text-foreground flex items-center gap-1.5">
                      <Layers className="w-4 h-4 text-primary flex-shrink-0" /> {it.name}
                    </div>
                    {it.description && <p className="text-xs text-muted-foreground mt-1">{it.description}</p>}
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {it.accountable_owner && <Chip>Owner: {it.accountable_owner}</Chip>}
                      <Chip tone={it.state === 'completed' ? 'green' : it.state === 'archived' ? 'muted' : 'violet'}>{(it.state || 'active')}</Chip>
                      {typeof roll.needs_attention === 'number' && <Chip tone={roll.needs_attention > 0 ? 'amber' : 'green'}>{roll.needs_attention} need attention · {roll.addressing || 0} addressed</Chip>}
                    </div>
                  </div>
                  {canWrite && (
                    <div className="flex gap-1 flex-shrink-0">
                      <Button size="sm" variant="outline" onClick={() => routeAll(it)} disabled={busy === it.initiative_id || materialCount === 0} data-testid={`initiative-route-${it.initiative_id}`}>
                        {busy === it.initiative_id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />} Route all ({materialCount})
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(it)} data-testid={`initiative-delete-${it.initiative_id}`}><Trash2 className="w-3.5 h-3.5 text-rose-500" /></Button>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-1.5 mt-2">
                  <Chip tone="violet">{roll.total || 0} recommendations</Chip>
                  {Object.entries(appr).map(([k, v]) => <Chip key={k} tone={k === 'approved' || k === 'actioned' ? 'green' : k === 'pending_approval' ? 'amber' : 'muted'}>{APPR_LABEL[k] || k}: {v}</Chip>)}
                  {Object.entries(out).map(([k, v]) => <Chip key={k} tone={k === 'successful' ? 'green' : k === 'unsuccessful' ? 'amber' : 'muted'}><Target className="w-3 h-3" /> {k}: {v}</Chip>)}
                </div>

                <div className="mt-2 space-y-1" data-testid={`initiative-members-${it.initiative_id}`}>
                  {(it.members || []).map((m) => (
                    <div key={m.rec_id} className="flex items-center justify-between gap-2 text-[11px] rounded-lg border border-border/30 px-2 py-1.5">
                      <span className="truncate text-foreground">{m.title}</span>
                      <span className="flex items-center gap-1.5 flex-shrink-0">
                        {m.material ? <Chip tone="amber">Material</Chip> : <Chip>Advisory</Chip>}
                        {m.approval_status && m.approval_status !== 'none' && <Chip tone="green">{APPR_LABEL[m.approval_status] || m.approval_status}</Chip>}
                        {m.outcome_status && <Chip tone={m.outcome_status === 'successful' ? 'green' : 'muted'}>{m.outcome_status}</Chip>}
                      </span>
                    </div>
                  ))}
                </div>
                <button onClick={() => navigate('/recommendations')} className="mt-2 inline-flex items-center gap-1 text-[11px] text-primary hover:underline" data-testid={`initiative-open-hub-${it.initiative_id}`}>
                  <ExternalLink className="w-3 h-3" /> Open in Governed Recommendations
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
